import React, { Component } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { Label, Col, Row } from "reactstrap";
import { BillTariffModel, CreateBillTariffRequest, UpdateBillTariffRequest, ScaleTier } from "../../models/BillTariff";
import { AppConstants, DEFAULT_CURRENCY } from "../../models/AppConstants";
import tariffApiService from "../../services/api/tariff/TariffApiService";
import BillTariffVersionsList from "../BillTariff/BillTariffVersionsList";
import { merchantVendorApiService } from "../../services/api/merchants/MerchantVendorApiService";
import { MerchantVendorResponseModel, MerchantVendorServiceResponseModel } from "../../models/MerchantVendor";
import { isSuperAdmin } from "../../authGuard";
import { storageService } from "../../services/StorageService";
import ExtendedDropdown from "../ExtendedDropdown";
import { SelectItem } from "../../models/SelectItem";
import { TariffCategory } from "../../models/TariffCategory";
import { Calendar as CalendarIcon } from "lucide-react";

const tariffTypeOptions = [
    { label: "PERCENTAGE", value: "PERCENTAGE" },
    { label: "FIXED", value: "FIXED" },
    { label: "SCALE", value: "SCALE" },
];

const emptyScaleTier: ScaleTier = { minValue: 0, maxValue: 0, tariffAmount: 0 };

interface UtilityBillsTariffModalProps {
    visible: boolean;
    isEditMode: boolean;
    selectedBillTariff: BillTariffModel | null;
    onHide: () => void;
    onSuccess: () => void; // Callback after successful save
}

interface UtilityBillsTariffModalState {
    formData: {
        billTypeCode: string;
        vendorId: number | null;
        serviceId: number | null;
        tariffType: string;
        value: number;
        valueString: string;
        scaleTiers: ScaleTier[];
        currency: string;
        effectiveFrom: Date | null;
        effectiveTo: Date | null;
        currentEffectiveFrom: Date | null;
        currentEffectiveTo: Date | null;
        originalCurrentEffectiveTo: Date | null;
        newEffectiveFrom: Date | null;
        newEffectiveTo: Date | null;
    };
    formErrors: {
        billTypeCode?: string;
        vendorId?: string;
        serviceId?: string;
        tariffType?: string;
        value?: string;
        effectiveFrom?: string;
        effectiveTo?: string;
        newEffectiveFrom?: string;
        newEffectiveTo?: string;
    };
    isLoading: boolean;
    vendors: MerchantVendorResponseModel[];
    services: MerchantVendorServiceResponseModel[];
    isLoadingVendors: boolean;
    isLoadingServices: boolean;
}

export default class UtilityBillsTariffModal extends Component<UtilityBillsTariffModalProps, UtilityBillsTariffModalState> {
    private toast: Toast | null = null;

    constructor(props: UtilityBillsTariffModalProps) {
        super(props);
        this.state = {
            formData: {
                billTypeCode: "",
                vendorId: null,
                serviceId: null,
                tariffType: "",
                value: 0,
                valueString: "",
                scaleTiers: [],
                currency: DEFAULT_CURRENCY,
                effectiveFrom: new Date(),
                effectiveTo: null,
                currentEffectiveFrom: null,
                currentEffectiveTo: null,
                originalCurrentEffectiveTo: null,
                newEffectiveFrom: null,
                newEffectiveTo: null,
            },
            formErrors: {},
            isLoading: false,
            vendors: [],
            services: [],
            isLoadingVendors: false,
            isLoadingServices: false,
        };
    }

    componentDidUpdate(prevProps: UtilityBillsTariffModalProps) {
        // Initialize form data when dialog opens or selectedBillTariff changes
        if (this.props.visible && !prevProps.visible) {
            if (this.props.isEditMode && this.props.selectedBillTariff) {
                // In edit mode, no API calls needed - everything comes from backend
                this.initializeEditMode(this.props.selectedBillTariff);
            } else {
                // In create mode, load vendors for dropdown selection
                this.loadVendors().then(() => {
                    this.initializeCreateMode();
                });
            }
        }
    }

    loadVendors = async () => {
        this.setState({ isLoadingVendors: true });
        try {
            let result;
            if (isSuperAdmin()) {
                // Super admin: call API with merchantId
                const merchantId = Number(storageService.getSelectedMerchantId());
                result = await merchantVendorApiService.getVendors(merchantId);
            } else {
                // Non-super admin: call own vendors API
                result = await merchantVendorApiService.getOwnVendors();
            }
            
            if (result.success && result.data) {
                this.setState({ vendors: result.data });
            } else {
                this.toast?.show({ severity: "error", summary: "Error", detail: result.message || "Failed to load vendors", life: 3000 });
            }
        } catch (error) {
            this.toast?.show({ severity: "error", summary: "Error", detail: "Failed to load vendors", life: 3000 });
        } finally {
            this.setState({ isLoadingVendors: false });
        }
    };

    loadServices = async (vendorId: number) => {
        this.setState({ isLoadingServices: true, services: [] });
        try {
            const result = await merchantVendorApiService.getVendorServices(vendorId);
            if (result.success && result.data) {
                this.setState({ services: result.data });
            } else {
                this.toast?.show({ severity: "error", summary: "Error", detail: result.message || "Failed to load services", life: 3000 });
            }
        } catch (error) {
            this.toast?.show({ severity: "error", summary: "Error", detail: "Failed to load services", life: 3000 });
        } finally {
            this.setState({ isLoadingServices: false });
        }
    };

    handleVendorChange = (vendorId: number | null) => {
        this.setState({
            formData: {
                ...this.state.formData,
                vendorId: vendorId,
                serviceId: null, // Reset service when vendor changes
                billTypeCode: "", // Reset bill type code when vendor changes
            },
            formErrors: {
                ...this.state.formErrors,
                vendorId: undefined,
                serviceId: undefined,
                billTypeCode: undefined,
            },
        });

        if (vendorId) {
            this.loadServices(vendorId);
        } else {
            this.setState({ services: [] });
        }
    };

    handleServiceChange = (serviceId: number | null) => {
        const selectedService = this.state.services.find(s => s.id === serviceId);
        // Bill Type Code will be set from service code (which represents the previous Bill Type Code)
        const billTypeCode = (selectedService as any)?.code || "";
        
        this.setState({
            formData: {
                ...this.state.formData,
                serviceId: serviceId,
                billTypeCode: billTypeCode,
            },
            formErrors: {
                ...this.state.formErrors,
                serviceId: undefined,
                billTypeCode: undefined,
            },
        });
    };

    // Convert vendors to SelectItem format for ExtendedDropdown
    getVendorOptions = (): SelectItem<number>[] => {
        const options = this.state.vendors.map(v => ({
            label: v.nameEN || v.nameBG || `Vendor ${v.id}`,
            value: v.id
        }));
        
        // In edit mode, if we have vendorName from backend but no vendors loaded, add it for display
        if (this.props.isEditMode && this.props.selectedBillTariff?.vendorName && options.length === 0) {
            // Create a dummy option for display purposes only
            return [{
                label: this.props.selectedBillTariff.vendorName,
                value: 0 // Dummy value, not used since dropdown is disabled
            }];
        }
        
        return options;
    };

    // Convert services to SelectItem format for ExtendedDropdown
    getServiceOptions = (): SelectItem<number>[] => {
        const options = this.state.services.map(s => {
            const serviceCode = (s as any)?.code;
            const serviceName = s.nameEN || s.nameBG || serviceCode || `Service ${s.id}`;
            return {
                label: serviceCode ? `${serviceName} (${serviceCode})` : serviceName,
                value: s.id
            };
        });
        
        // In edit mode, if we have serviceName from backend but no services loaded, add it for display
        if (this.props.isEditMode && this.props.selectedBillTariff) {
            const billTariff = this.props.selectedBillTariff;
            const serviceName = billTariff.serviceName;
            const serviceCode = billTariff.serviceCode;
            const serviceId = billTariff.serviceId;
            
            if (serviceId && options.length === 0 && serviceName) {
                const displayLabel = serviceCode ? `${serviceName} (${serviceCode})` : serviceName;
                return [{
                    label: displayLabel,
                    value: serviceId
                }];
            }
        }
        
        return options;
    };

    initializeCreateMode = () => {
        this.setState({
            formData: {
                billTypeCode: "",
                vendorId: null,
                serviceId: null,
                tariffType: "",
                value: 0,
                valueString: "",
                scaleTiers: [],
                currency: DEFAULT_CURRENCY,
                effectiveFrom: new Date(),
                effectiveTo: null,
                currentEffectiveFrom: null,
                currentEffectiveTo: null,
                originalCurrentEffectiveTo: null,
                newEffectiveFrom: null,
                newEffectiveTo: null,
            },
            formErrors: {},
            isLoading: false,
            services: [],
        });
    };

    initializeEditMode = async (billTariff: BillTariffModel) => {
        const effectiveFromDate = billTariff.effectiveFrom ? new Date(billTariff.effectiveFrom) : new Date();
        let numericValue = 0;
        let valueString = "";
        let scaleTiers: ScaleTier[] = [];

        if (billTariff.tariffType === "SCALE") {
            try {
                scaleTiers = JSON.parse(billTariff.value) as ScaleTier[];
                scaleTiers = scaleTiers.map(tier => ({
                    ...tier,
                    tariffAmountString: tier.tariffAmount === 0 ? "" : tier.tariffAmount.toFixed(2),
                    minValueString: tier.minValue === 0 ? "" : tier.minValue.toString(),
                    maxValueString: tier.maxValue === 0 ? "" : tier.maxValue.toString(),
                }));
            } catch {
                scaleTiers = [{ ...emptyScaleTier }];
            }
        } else {
            numericValue = parseFloat(billTariff.value) || 0;
            valueString = billTariff.value;
        }

        const effectiveToDate = billTariff.effectiveTo ? new Date(billTariff.effectiveTo) : null;

        // Use serviceId directly from backend - everything comes from backend
        // No API calls needed in edit mode - vendor/service/billTypeCode are for display only
        const serviceId: number | null = billTariff.serviceId || null;
        // Use dummy vendorId (0) for display purposes in edit mode - vendorName comes from backend
        const vendorId: number | null = null;

        this.setState({
            formData: {
                billTypeCode: billTariff.billTypeCode,
                vendorId: vendorId, // Not needed in edit mode - dropdowns are disabled, vendorName shown from backend
                serviceId: serviceId, // Use serviceId directly from backend
                tariffType: billTariff.tariffType,
                value: numericValue,
                valueString: valueString,
                scaleTiers: scaleTiers,
                currency: billTariff.currency || DEFAULT_CURRENCY,
                effectiveFrom: null,
                effectiveTo: null,
                currentEffectiveFrom: effectiveFromDate,
                currentEffectiveTo: effectiveToDate,
                originalCurrentEffectiveTo: effectiveToDate ? new Date(effectiveToDate) : null,
                newEffectiveFrom: null,
                newEffectiveTo: null,
            },
            formErrors: {},
            isLoading: false,
        });
    };

    validateForm = (): boolean => {
        const errors: any = {};

        // For Utility Bills Tariff, validate Vendor and Service instead of Bill Type Code input
        if (!this.props.isEditMode) {
            if (!this.state.formData.vendorId) {
                errors.vendorId = "Vendor is required";
            }
            if (!this.state.formData.serviceId) {
                errors.serviceId = "Service is required";
            }
        }

        // Bill Type Code is always required (auto-populated from service or existing value)
        if (!this.state.formData.billTypeCode.trim()) {
            errors.billTypeCode = "Bill Type Code is required";
        } else if (this.state.formData.billTypeCode.length >= 20) {
            errors.billTypeCode = "Bill Type Code must be less than 20 characters";
        }

        if (!this.state.formData.tariffType) {
            errors.tariffType = "Tariff Type is required";
        }

        if (this.state.formData.tariffType === "SCALE") {
            const tiers = this.state.formData.scaleTiers;
            if (tiers.length === 0) {
                errors.value = "At least one tier is required";
            } else {
                for (let i = 0; i < tiers.length; i++) {
                    const tier = tiers[i];
                    
                    // Validate MinValue is required and must be > 0
                    if (tier.minValue <= 0) {
                        errors.value = `Tier ${i + 1}: Min Value must be greater than 0`;
                        break;
                    }
                    
                    // Validate MaxValue: must be > 0 and > MinValue
                    if (tier.maxValue <= 0) {
                        errors.value = `Tier ${i + 1}: Max Value must be greater than 0`;
                        break;
                    }
                    // MaxValue must be greater than MinValue
                    if (tier.maxValue <= tier.minValue) {
                        errors.value = `Tier ${i + 1}: Max Value must be greater than Min Value`;
                        break;
                    }
                    if (tier.maxValue !== 0 && tier.maxValue > AppConstants.ScaleTierMaxValue) {
                        errors.value = `Tier ${i + 1}: Max Value must be less than ${AppConstants.ScaleTierMaxValue.toLocaleString()}`;
                        break;
                    }
                    
                    // Validate TariffAmount is required and must be > 0
                    if (tier.tariffAmount <= 0 || tier.tariffAmount > AppConstants.ScaleTierMaxTariffAmount) {
                        errors.value = `Tier ${i + 1}: Tariff Amount must be greater than 0 and less than or equal to ${AppConstants.ScaleTierMaxTariffAmount}%`;
                        break;
                    }
                    
                    // Validate tier continuity (each tier's MinValue equals previous tier's MaxValue)
                    if (i > 0 && tier.minValue !== tiers[i - 1].maxValue) {
                        errors.value = `Tier ${i + 1}: Min Value must equal previous tier's Max Value (${tiers[i - 1].maxValue})`;
                        break;
                    }
                }
            }
        } else if (this.state.formData.tariffType === "PERCENTAGE") {
            if (this.state.formData.value === null || this.state.formData.value === undefined || this.state.formData.value <= 0) {
                errors.value = "Value must be a positive number";
            } else if (this.state.formData.value > AppConstants.ScaleTierMaxTariffAmount) {
                errors.value = `Value must be between 0 and ${AppConstants.ScaleTierMaxTariffAmount} (percentage)`;
            } else {
                const valueStr = this.state.formData.value.toString();
                if (valueStr.length >= 7) {
                    errors.value = "Value must be less than 7 characters";
                }
                const decimalParts = valueStr.split('.');
                if (decimalParts.length > 1 && decimalParts[1].length > 2) {
                    errors.value = "Value can have maximum 2 decimal places";
                }
            }
        } else if (this.state.formData.tariffType === "FIXED") {
            if (this.state.formData.value === null || this.state.formData.value === undefined || this.state.formData.value <= 0) {
                errors.value = "Value must be a positive number";
            } else if (this.state.formData.value > AppConstants.FixedTariffMaxValue) {
                errors.value = `Value must be between 0 and ${AppConstants.FixedTariffMaxValue}`;
            } else {
                const valueStr = this.state.formData.value.toString();
                if (valueStr.length >= 7) {
                    errors.value = "Value must be less than 7 characters";
                }
                const decimalParts = valueStr.split('.');
                if (decimalParts.length > 1 && decimalParts[1].length > 2) {
                    errors.value = "Value can have maximum 2 decimal places";
                }
            }
        }

        if (this.props.isEditMode) {
            if (!this.state.formData.newEffectiveFrom) {
                errors.newEffectiveFrom = "New Effective From date is required";
            } else {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const newEffectiveFromDate = new Date(this.state.formData.newEffectiveFrom);
                newEffectiveFromDate.setHours(0, 0, 0, 0);

                if (newEffectiveFromDate < today) {
                    errors.newEffectiveFrom = "New Effective From date cannot be in the past";
                }
                
                // New Effective From can be up to one day after Current Effective To (from database), but not more
                if (this.state.formData.originalCurrentEffectiveTo) {
                    const originalCurrentEffectiveToDate = new Date(this.state.formData.originalCurrentEffectiveTo);
                    originalCurrentEffectiveToDate.setHours(0, 0, 0, 0);
                    const maxAllowedDate = new Date(originalCurrentEffectiveToDate);
                    maxAllowedDate.setDate(maxAllowedDate.getDate() + 1); // One day after Current Effective To
                    
                    if (newEffectiveFromDate > maxAllowedDate) {
                        errors.newEffectiveFrom = `New Effective From date cannot be more than one day after Current Effective To date (max: ${maxAllowedDate.toLocaleDateString('en-GB')})`;
                    }
                }
            }

            if (!this.state.formData.newEffectiveTo) {
                errors.newEffectiveTo = "New Effective To date is required";
            } else if (this.state.formData.newEffectiveFrom && this.state.formData.newEffectiveTo) {
                if (this.state.formData.newEffectiveTo <= this.state.formData.newEffectiveFrom) {
                    errors.newEffectiveTo = "New Effective To date must be greater than New Effective From date";
                }
            }
        } else {
            if (!this.state.formData.effectiveFrom) {
                errors.effectiveFrom = "Effective From date is required";
            }

            if (!this.state.formData.effectiveTo) {
                errors.effectiveTo = "Effective To date is required";
            } else if (this.state.formData.effectiveFrom && this.state.formData.effectiveTo) {
                if (this.state.formData.effectiveTo <= this.state.formData.effectiveFrom) {
                    errors.effectiveTo = "Effective To date must be greater than Effective From date";
                }
            }
        }

        this.setState({ formErrors: errors });
        return Object.keys(errors).length === 0;
    };

    getSerializedValue = (): string => {
        if (this.state.formData.tariffType === "SCALE") {
            // Filter out optional string properties before serializing
            const cleanTiers = this.state.formData.scaleTiers.map(tier => ({
                minValue: tier.minValue,
                maxValue: tier.maxValue,
                tariffAmount: tier.tariffAmount,
            }));
            return JSON.stringify(cleanTiers);
        }
        if (this.state.formData.valueString && this.state.formData.valueString.trim() !== "") {
            const numValue = parseFloat(this.state.formData.valueString);
            if (!isNaN(numValue) && numValue > 0) {
                return numValue.toFixed(2);
            }
        }
        if (this.state.formData.value > 0) {
            return this.state.formData.value.toFixed(2);
        }
        return "0.00";
    };

    hasFormChanged = (): boolean => {
        if (!this.props.isEditMode || !this.props.selectedBillTariff) {
            return true;
        }

        const original = this.props.selectedBillTariff;
        const current = this.state.formData;

        if (original.tariffType !== current.tariffType) {
            return true;
        }

        const currentSerializedValue = this.getSerializedValue();
        if (original.value !== currentSerializedValue) {
            return true;
        }

        if (this.props.isEditMode) {
            const originalDate = original.effectiveFrom ? new Date(original.effectiveFrom) : null;
            const currentDate = current.newEffectiveFrom;

            if (!originalDate && !currentDate) {
            } else if (!originalDate || !currentDate) {
                return true;
            } else {
                const originalDateStr = `${originalDate.getFullYear()}-${String(originalDate.getMonth() + 1).padStart(2, '0')}-${String(originalDate.getDate()).padStart(2, '0')}`;
                const currentDateStr = currentDate ? `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}` : "";

                if (originalDateStr !== currentDateStr) {
                    return true;
                }
            }

            const originalEffectiveTo = original.effectiveTo ? new Date(original.effectiveTo) : null;
            const currentEffectiveTo = current.newEffectiveTo;

            if (!originalEffectiveTo && !currentEffectiveTo) {
            } else if (!originalEffectiveTo || !currentEffectiveTo) {
                return true;
            } else {
                const originalEffectiveToStr = `${originalEffectiveTo.getFullYear()}-${String(originalEffectiveTo.getMonth() + 1).padStart(2, '0')}-${String(originalEffectiveTo.getDate()).padStart(2, '0')}`;
                const currentEffectiveToStr = currentEffectiveTo ? `${currentEffectiveTo.getFullYear()}-${String(currentEffectiveTo.getMonth() + 1).padStart(2, '0')}-${String(currentEffectiveTo.getDate()).padStart(2, '0')}` : "";

                if (originalEffectiveToStr !== currentEffectiveToStr) {
                    return true;
                }
            }
        }

        return false;
    };

    handleSave = async () => {
        if (!this.validateForm()) {
            return;
        }

        if (this.props.isEditMode && !this.hasFormChanged()) {
            return;
        }

        this.setState({ isLoading: true });

        try {
            const serializedValue = this.getSerializedValue();

            if (this.props.isEditMode && this.props.selectedBillTariff) {
                const effectiveFromDate = this.state.formData.newEffectiveFrom!;
                const effectiveFromFormatted = `${effectiveFromDate.getFullYear()}-${String(effectiveFromDate.getMonth() + 1).padStart(2, '0')}-${String(effectiveFromDate.getDate()).padStart(2, '0')}`;
                
                const effectiveToDate = this.state.formData.newEffectiveTo!;
                const effectiveToFormatted = `${effectiveToDate.getFullYear()}-${String(effectiveToDate.getMonth() + 1).padStart(2, '0')}-${String(effectiveToDate.getDate()).padStart(2, '0')}`;

                const updateRequest: UpdateBillTariffRequest = {
                    id: this.props.selectedBillTariff.id,
                    tariffCategory: TariffCategory.UtilityBillsTariff, // Always use category 2 for Utility Bills Tariff
                    serviceId: this.state.formData.serviceId, // Include serviceId for Utility Bills Tariff
                    tariffType: this.state.formData.tariffType,
                    value: serializedValue,
                    currency: this.state.formData.currency,
                    effectiveFrom: effectiveFromFormatted,
                    effectiveTo: effectiveToFormatted,
                };

                const result = await tariffApiService.updateBillTariff(updateRequest);

                if (!result.success) {
                    this.toast?.show({ severity: "error", summary: "Error", detail: result.message, life: 3000 });
                    this.setState({ isLoading: false });
                    return;
                }

                this.toast?.show({ severity: "success", summary: "Success", detail: "Utility Bills Tariff updated successfully", life: 3000 });
                this.setState({ isLoading: false });
            } else {
                const effectiveFromDate = this.state.formData.effectiveFrom!;
                const effectiveFromFormatted = `${effectiveFromDate.getFullYear()}-${String(effectiveFromDate.getMonth() + 1).padStart(2, '0')}-${String(effectiveFromDate.getDate()).padStart(2, '0')}`;
                
                const effectiveToDate = this.state.formData.effectiveTo!;
                const effectiveToFormatted = `${effectiveToDate.getFullYear()}-${String(effectiveToDate.getMonth() + 1).padStart(2, '0')}-${String(effectiveToDate.getDate()).padStart(2, '0')}`;

                const createRequest: CreateBillTariffRequest = {
                    billTypeCode: this.state.formData.billTypeCode,
                    tariffCategory: TariffCategory.UtilityBillsTariff, // Always use category 2 for Utility Bills Tariff
                    serviceId: this.state.formData.serviceId, // Include serviceId for Utility Bills Tariff
                    tariffType: this.state.formData.tariffType,
                    value: serializedValue,
                    currency: this.state.formData.currency,
                    effectiveFrom: effectiveFromFormatted,
                    effectiveTo: effectiveToFormatted,
                };

                const result = await tariffApiService.createBillTariff(createRequest);

                if (!result.success) {
                    this.toast?.show({ severity: "error", summary: "Error", detail: result.message, life: 3000 });
                    this.setState({ isLoading: false });
                    return;
                }

                this.toast?.show({ severity: "success", summary: "Success", detail: "Utility Bills Tariff created successfully", life: 3000 });
                this.setState({ isLoading: false });
            }

            this.props.onSuccess();
            this.props.onHide();
        } catch (error) {
            this.toast?.show({ severity: "error", summary: "Error", detail: "An error occurred while saving", life: 3000 });
            this.setState({ isLoading: false });
        }
    };

    updateScaleTier = (index: number, field: keyof ScaleTier, rawValue: string) => {
        const tiers = [...this.state.formData.scaleTiers];
        const numValue = rawValue === "" ? 0 : parseFloat(rawValue);
        
        if (field === "tariffAmount") {
            if (rawValue === "") {
                tiers[index] = { 
                    ...tiers[index], 
                    tariffAmount: 0,
                    tariffAmountString: ""
                };
            } else {
                const parsed = parseFloat(rawValue);
                if (!isNaN(parsed)) {
                    tiers[index] = {
                        ...tiers[index],
                        tariffAmount: parsed,
                        tariffAmountString: rawValue,
                    };
                }
            }
        } else if (field === "minValue") {
            if (rawValue === "" || rawValue === ".") {
                // Don't update if empty, keep current value
                return;
            }
            const parsed = parseFloat(rawValue);
            if (!isNaN(parsed) && parsed > 0) {
                // Only update the current tier's minValue during input
                // Tier continuity will be validated on blur or save
                tiers[index] = { ...tiers[index], minValue: parsed };
            }
        } else if (field === "maxValue") {
            if (rawValue === "" || rawValue === ".") {
                // Don't update if empty, keep current value
                return;
            }
            const parsed = parseFloat(rawValue);
            if (!isNaN(parsed) && parsed > 0) {
                tiers[index] = { ...tiers[index], maxValue: parsed };
                if (index < tiers.length - 1) {
                    tiers[index + 1] = { ...tiers[index + 1], minValue: parsed };
                }
            }
        }

        this.setState({
            formData: { ...this.state.formData, scaleTiers: tiers },
        });
    };

    addScaleTier = () => {
        const tiers = [...this.state.formData.scaleTiers];
        const lastTier = tiers.length > 0 ? tiers[tiers.length - 1] : null;
        // Start with empty values that must be filled (not 0)
        const newTier: ScaleTier = {
            minValue: lastTier ? lastTier.maxValue : 0, // Will be set from previous tier's maxValue
            maxValue: 0, // Must be filled by user (> 0)
            tariffAmount: 0, // Must be filled by user (> 0)
        };
        tiers.push(newTier);
        this.setState({
            formData: { ...this.state.formData, scaleTiers: tiers },
        });
    };

    removeScaleTier = (index: number) => {
        const tiers = [...this.state.formData.scaleTiers];
        tiers.splice(index, 1);
        this.setState({
            formData: { ...this.state.formData, scaleTiers: tiers },
        });
    };

    handleTariffTypeChange = (newType: string) => {
        if (newType === "SCALE") {
            const scaleTiers = this.state.formData.scaleTiers.length > 0 
                ? this.state.formData.scaleTiers 
                : [{ ...emptyScaleTier }];
            
            this.setState({
                formData: {
                    ...this.state.formData,
                    tariffType: newType,
                    value: 0,
                    valueString: "",
                    scaleTiers: scaleTiers,
                },
                formErrors: {
                    ...this.state.formErrors,
                    tariffType: undefined,
                    value: undefined,
                },
            });
        } else {
            this.setState({
                formData: {
                    ...this.state.formData,
                    tariffType: newType,
                    value: 0,
                    valueString: "",
                    scaleTiers: [],
                },
                formErrors: {
                    ...this.state.formErrors,
                    tariffType: undefined,
                    value: undefined,
                },
            });
        }
    };

    renderScaleTiersTable = () => {
        const { formData, formErrors } = this.state;
        const tiers = formData.scaleTiers;

        return (
            <div>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #dee2e6" }}>#</th>
                            <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #dee2e6" }}>Min Value</th>
                            <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #dee2e6" }}>Max Value</th>
                            <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #dee2e6" }}>Tariff Amount (%)</th>
                            <th style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #dee2e6" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tiers.map((tier, index) => (
                            <tr key={index}>
                                <td style={{ padding: "8px", borderBottom: "1px solid #dee2e6" }}>{index + 1}</td>
                                <td style={{ padding: "8px", borderBottom: "1px solid #dee2e6" }}>
                                    <InputText
                                        type="text"
                                        value={tier.minValue > 0 ? tier.minValue.toString() : ""}
                                        onChange={(e) => this.updateScaleTier(index, "minValue", e.target.value)}
                                        onBlur={(e) => {
                                            const inputValue = e.target.value;
                                            if (inputValue === "" || inputValue === ".") {
                                                if (tier.minValue <= 0) {
                                                    this.setState({
                                                        formErrors: {
                                                            ...formErrors,
                                                            value: `Tier ${index + 1}: Min Value must be greater than 0`,
                                                        },
                                                    });
                                                }
                                                return;
                                            }
                                            const numValue = parseFloat(inputValue);
                                            if (!isNaN(numValue) && numValue > 0) {
                                                const tiers = [...this.state.formData.scaleTiers];
                                                tiers[index] = { ...tiers[index], minValue: numValue };
                                                
                                                // Validate tier continuity: minValue should equal previous tier's maxValue
                                                if (index > 0 && numValue !== tiers[index - 1].maxValue) {
                                                    // Auto-fix: set minValue to previous tier's maxValue
                                                    tiers[index] = { ...tiers[index], minValue: tiers[index - 1].maxValue };
                                                }
                                                
                                                // Update next tier's minValue if this tier's maxValue is set
                                                if (index < tiers.length - 1 && tiers[index].maxValue > 0) {
                                                    tiers[index + 1] = { ...tiers[index + 1], minValue: tiers[index].maxValue };
                                                }
                                                
                                                this.setState({
                                                    formData: { ...this.state.formData, scaleTiers: tiers },
                                                    formErrors: { ...this.state.formErrors, value: undefined },
                                                });
                                            } else {
                                                this.setState({
                                                    formErrors: {
                                                        ...formErrors,
                                                        value: `Tier ${index + 1}: Min Value must be greater than 0`,
                                                    },
                                                });
                                            }
                                        }}
                                        style={{ width: "100%" }}
                                        disabled={index > 0}
                                    />
                                </td>
                                <td style={{ padding: "8px", borderBottom: "1px solid #dee2e6" }}>
                                    <InputText
                                        type="text"
                                        value={tier.maxValue > 0 ? tier.maxValue.toString() : ""}
                                        onChange={(e) => this.updateScaleTier(index, "maxValue", e.target.value)}
                                        onBlur={(e) => {
                                            const inputValue = e.target.value;
                                            if (inputValue === "" || inputValue === ".") {
                                                // Don't set to 0, keep current value or show error
                                                if (tier.maxValue <= 0) {
                                                    this.setState({
                                                        formErrors: {
                                                            ...formErrors,
                                                            value: `Tier ${index + 1}: Max Value must be greater than 0`,
                                                        },
                                                    });
                                                }
                                                return;
                                            }
                                            const numValue = parseFloat(inputValue);
                                            if (!isNaN(numValue)) {
                                                // MaxValue must be > 0
                                                if (numValue <= 0) {
                                                    this.setState({
                                                        formErrors: {
                                                            ...formErrors,
                                                            value: `Tier ${index + 1}: Max Value must be greater than 0`,
                                                        },
                                                    });
                                                    return;
                                                }
                                                if (numValue > 0 && numValue < AppConstants.ScaleTierMaxValue) {
                                                    const formatted = numValue.toString();
                                                    this.updateScaleTier(index, "maxValue", formatted);
                                                } else {
                                                    const currentStateValue = tier.maxValue;
                                                    if (currentStateValue > 0 && currentStateValue < AppConstants.ScaleTierMaxValue) {
                                                        this.setState({
                                                            formErrors: { ...formErrors, value: undefined },
                                                        });
                                                        const formatted = currentStateValue.toString();
                                                        this.updateScaleTier(index, "maxValue", formatted);
                                                    }
                                                }
                                            }
                                        }}
                                        style={{ width: "100%" }}
                                    />
                                </td>
                                <td style={{ padding: "8px", borderBottom: "1px solid #dee2e6" }}>
                                    <InputText
                                        type="text"
                                        value={tier.tariffAmountString !== undefined ? tier.tariffAmountString : (tier.tariffAmount === 0 ? "" : tier.tariffAmount.toFixed(2))}
                                        onChange={(e) => {
                                            const inputValue = e.target.value;

                                            if (inputValue === "") {
                                                this.updateScaleTier(index, "tariffAmount", inputValue);
                                                return;
                                            }

                                            if (/^\d*\.?\d{0,2}$/.test(inputValue)) {
                                                const numValue = parseFloat(inputValue);
                                                if (!isNaN(numValue) && numValue >= 0) {
                                                    if (numValue > AppConstants.ScaleTierMaxTariffAmount) {
                                                        this.setState({
                                                            formErrors: {
                                                                ...formErrors,
                                                                value: `Tier ${index + 1}: Tariff Amount cannot exceed ${AppConstants.ScaleTierMaxTariffAmount}%`,
                                                            },
                                                        });
                                                        return;
                                                    }
                                                    
                                                    this.setState({
                                                        formErrors: {
                                                            ...formErrors,
                                                            value: undefined,
                                                        },
                                                    });
                                                    
                                                    this.updateScaleTier(index, "tariffAmount", inputValue);
                                                } else if (inputValue === "." || inputValue.endsWith(".")) {
                                                    this.updateScaleTier(index, "tariffAmount", inputValue);
                                                }
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const inputValue = e.target.value;
                                            if (inputValue === "" || inputValue === ".") {
                                                this.updateScaleTier(index, "tariffAmount", "0");
                                            } else {
                                                const numValue = parseFloat(inputValue);
                                                if (!isNaN(numValue)) {
                                                    if (numValue >= 0 && numValue <= AppConstants.ScaleTierMaxTariffAmount) {
                                                        const formatted = numValue.toFixed(2);
                                                        this.updateScaleTier(index, "tariffAmount", formatted);
                                                    } else {
                                                        const currentStateValue = tier.tariffAmount;
                                                        if (currentStateValue >= 0 && currentStateValue <= AppConstants.ScaleTierMaxTariffAmount) {
                                                            this.setState({
                                                                formErrors: { ...formErrors, value: undefined },
                                                            });
                                                            const formatted = currentStateValue.toFixed(2);
                                                            this.updateScaleTier(index, "tariffAmount", formatted);
                                                        }
                                                    }
                                                }
                                            }
                                        }}
                                        placeholder="0.00"
                                        style={{ width: "100%" }}
                                    />
                                </td>
                                <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #dee2e6" }}>
                                    <Button
                                        type="button"
                                        icon="pi pi-trash"
                                        outlined
                                        severity="danger"
                                        size="small"
                                        onClick={() => this.removeScaleTier(index)}
                                        disabled={tiers.length <= 1}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {formErrors.value && (
                    <div style={{ marginTop: "0.5rem" }}>
                        <small className="p-error">{formErrors.value}</small>
                    </div>
                )}
                <div style={{ marginTop: "0.5rem" }}>
                    <Button
                        type="button"
                        label="Add Tier"
                        icon="pi pi-plus"
                        outlined
                        size="small"
                        onClick={this.addScaleTier}
                    />
                </div>
            </div>
        );
    };

    render() {
        const { visible, isEditMode, onHide } = this.props;
        const { formData, formErrors, isLoading } = this.state;
        const isScale = formData.tariffType === "SCALE";

        return (
            <>
                <Dialog
                    header={isEditMode ? "Edit Utility Bills Tariff" : "Create Utility Bills Tariff"}
                    visible={visible}
                    style={{ width: isScale ? "65vw" : "50vw", position: "relative" }}
                    contentStyle={{ maxHeight: "70vh", overflowY: "auto", position: "relative" }}
                    baseZIndex={1000}
                    onHide={onHide}
                    footer={
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                            <Button label="Cancel" icon="pi pi-times" outlined onClick={onHide} disabled={isLoading} />
                            <Button
                                label="Save"
                                icon={isLoading ? "pi pi-spin pi-spinner" : "pi pi-check"}
                                onClick={this.handleSave}
                                disabled={isLoading || (isEditMode && !this.hasFormChanged())}
                                loading={isLoading}
                            />
                        </div>
                    }
                >
                    <div style={{ padding: "1rem", position: "relative" }}>
                         <Toast ref={(el) => (this.toast = el)} position={"bottom-center"} baseZIndex={999999}></Toast>
                        <Row>
                            <Col md={6} style={{ marginBottom: "1rem" }}>
                                <Label htmlFor="vendor">
                                    Vendor {!isEditMode && <span style={{ color: "red" }}>*</span>}
                                </Label>
                                <div style={{ position: "relative" }}>
                                    <ExtendedDropdown
                                        name="vendor"
                                        value={isEditMode && this.props.selectedBillTariff?.vendorName ? 0 : formData.vendorId}
                                        options={this.getVendorOptions()}
                                        onChange={(e) => this.handleVendorChange(e.value)}
                                        placeholder="Select Vendor"
                                        disabled={isEditMode || this.state.isLoadingVendors}
                                        required={!isEditMode}
                                        filter={true}
                                        filterBy="label"
                                        onValidStatusChanged={(status: boolean) => {
                                            this.setState({
                                                formErrors: {
                                                    ...this.state.formErrors,
                                                    vendorId: status ? undefined : "Vendor is required"
                                                }
                                            });
                                        }}
                                    />
                                    {this.state.isLoadingVendors && (
                                        <div style={{ display: "flex", justifyContent: "center", marginTop: "8px" }}>
                                            <ProgressSpinner style={{ width: "20px", height: "20px" }} strokeWidth="3" />
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col md={6} style={{ marginBottom: "1rem" }}>
                                <Label htmlFor="service">
                                    Service {!isEditMode && <span style={{ color: "red" }}>*</span>}
                                </Label>
                                <div style={{ position: "relative" }}>
                                    <ExtendedDropdown
                                        name="service"
                                        value={formData.serviceId}
                                        options={this.getServiceOptions()}
                                        onChange={(e) => this.handleServiceChange(e.value)}
                                        placeholder="Select Service"
                                        disabled={isEditMode || !formData.vendorId || this.state.isLoadingServices}
                                        required={!isEditMode}
                                        filter={true}
                                        filterBy="label"
                                        onValidStatusChanged={(status: boolean) => {
                                            this.setState({
                                                formErrors: {
                                                    ...this.state.formErrors,
                                                    serviceId: status ? undefined : "Service is required"
                                                }
                                            });
                                        }}
                                    />
                                    {this.state.isLoadingServices && (
                                        <div style={{ display: "flex", justifyContent: "center", marginTop: "8px" }}>
                                            <ProgressSpinner style={{ width: "20px", height: "20px" }} strokeWidth="3" />
                                        </div>
                                    )}
                                </div>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6} style={{ marginBottom: "1rem" }}>
                                <Label htmlFor="billTypeCode">
                                    Bill Type Code <span style={{ color: "red" }}>*</span>
                                </Label>
                                <InputText
                                    id="billTypeCode"
                                    value={formData.billTypeCode}
                                    disabled={true}
                                    className={formErrors.billTypeCode ? "p-invalid" : ""}
                                    style={{ width: "100%" }}
                                />
                                {formErrors.billTypeCode && (
                                    <small className="p-error">{formErrors.billTypeCode}</small>
                                )}
                            </Col>
                            <Col md={6} style={{ marginBottom: "1rem" }}>
                                <Label htmlFor="currency">
                                    Currency <span style={{ color: "red" }}>*</span>
                                </Label>
                                <InputText
                                    id="currency"
                                    value={formData.currency}
                                    disabled={true}
                                    style={{ width: "100%" }}
                                />
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12} style={{ marginBottom: "1rem" }}>
                                <Label htmlFor="tariffType">
                                    Tariff Type <span style={{ color: "red" }}>*</span>
                                </Label>
                                <Dropdown
                                    id="tariffType"
                                    value={formData.tariffType}
                                    options={tariffTypeOptions}
                                    onChange={(e) => this.handleTariffTypeChange(e.value)}
                                    placeholder="Select Tariff Type"
                                    className={formErrors.tariffType ? "p-invalid" : ""}
                                    style={{ width: "100%" }}
                                />
                                {formErrors.tariffType && (
                                    <small className="p-error">{formErrors.tariffType}</small>
                                )}
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12} style={{ marginBottom: "1rem" }}>
                                <Label htmlFor="value">
                                    Value <span style={{ color: "red" }}>*</span>
                                </Label>
                                {isScale ? (
                                    this.renderScaleTiersTable()
                                ) : (
                                    <InputText
                                        id="value"
                                        type="text"
                                        value={formData.valueString}
                                        onChange={(e) => {
                                            const inputValue = e.target.value;

                                            if (inputValue === "") {
                                                this.setState({
                                                    formData: { ...formData, value: 0, valueString: "" },
                                                    formErrors: { ...formErrors, value: undefined },
                                                });
                                                return;
                                            }

                                            if (/^\d*\.?\d{0,2}$/.test(inputValue)) {
                                                if (inputValue.length < 7) {
                                                    const numValue = parseFloat(inputValue);
                                                    if (!isNaN(numValue) && numValue >= 0) {
                                                        if (formData.tariffType === "PERCENTAGE" && numValue > AppConstants.ScaleTierMaxTariffAmount) {
                                                            this.setState({
                                                                formErrors: {
                                                                    ...formErrors,
                                                                    value: `Value cannot exceed ${AppConstants.ScaleTierMaxTariffAmount}% for PERCENTAGE type`,
                                                                },
                                                            });
                                                            return;
                                                        }
                                                        
                                                        if (formData.tariffType === "FIXED" && numValue > AppConstants.FixedTariffMaxValue) {
                                                            this.setState({
                                                                formErrors: {
                                                                    ...formErrors,
                                                                    value: `Value cannot exceed ${AppConstants.FixedTariffMaxValue} for FIXED type`,
                                                                },
                                                            });
                                                            return;
                                                        }
                                                        
                                                        this.setState({
                                                            formErrors: { ...formErrors, value: undefined },
                                                            formData: { ...formData, value: numValue, valueString: inputValue },
                                                        });
                                                    }
                                                }
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const inputValue = e.target.value;
                                            if (inputValue === "" || inputValue === ".") {
                                                this.setState({
                                                    formData: { ...formData, value: 0, valueString: "" },
                                                });
                                            } else {
                                                const numValue = parseFloat(inputValue);
                                                if (!isNaN(numValue)) {
                                                    const formatted = numValue.toFixed(2);
                                                    this.setState({
                                                        formData: { ...formData, value: numValue, valueString: formatted },
                                                    });
                                                }
                                            }
                                        }}
                                        placeholder="0.00"
                                        className={formErrors.value ? "p-invalid" : ""}
                                        style={{ width: "100%" }}
                                    />
                                )}
                                {!isScale && formErrors.value && (
                                    <small className="p-error">{formErrors.value}</small>
                                )}
                            </Col>
                        </Row>

                        {isEditMode ? (
                            <>
                                <Row>
                                    <Col md={6} style={{ marginBottom: "1rem" }}>
                                        <Label htmlFor="currentEffectiveFrom">
                                            Current Effective From <span style={{ color: "red" }}>*</span>
                                        </Label>
                                        <InputText
                                            id="currentEffectiveFrom"
                                            value={formData.currentEffectiveFrom ? new Date(formData.currentEffectiveFrom).toLocaleDateString('en-GB') : ""}
                                            disabled={true}
                                            style={{ width: "100%" }}
                                        />
                                    </Col>
                                    <Col md={6} style={{ marginBottom: "1rem" }}>
                                        <Label htmlFor="currentEffectiveTo">
                                            Current Effective To <span style={{ color: "red" }}>*</span>
                                        </Label>
                                        <InputText
                                            id="currentEffectiveTo"
                                            value={formData.currentEffectiveTo ? new Date(formData.currentEffectiveTo).toLocaleDateString('en-GB') : ""}
                                            disabled={true}
                                            style={{ width: "100%" }}
                                        />
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6} style={{ marginBottom: "1rem" }}>
                                        <Label htmlFor="newEffectiveFrom">
                                            New Effective From <span style={{ color: "red" }}>*</span>
                                        </Label>
                                        <Calendar
                                            id="newEffectiveFrom"
                                            value={formData.newEffectiveFrom}
                                            onChange={(e) => {
                                                const newDate = e.value as Date;
                                                const currentEffectiveToDate = new Date(newDate);
                                                currentEffectiveToDate.setDate(currentEffectiveToDate.getDate() - 1);
                                                
                                                this.setState({
                                                    formData: {
                                                        ...formData,
                                                        newEffectiveFrom: newDate,
                                                        currentEffectiveTo: currentEffectiveToDate,
                                                    },
                                                    formErrors: { ...formErrors, newEffectiveFrom: undefined },
                                                });
                                            }}
                                            dateFormat="dd/mm/yy"
                                            placeholder="dd/mm/yy"
                                            showIcon
                                            icon={<CalendarIcon size={16} />}
                                            appendTo={document.body}
                                            baseZIndex={1100}
                                            minDate={(() => {
                                                // Allow selecting any date >= today
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                return today;
                                            })()}
                                            maxDate={(() => {
                                                // New Effective From can be up to one day after Current Effective To (from database)
                                                if (formData.originalCurrentEffectiveTo) {
                                                    const originalCurrentEffectiveToDate = new Date(formData.originalCurrentEffectiveTo);
                                                    originalCurrentEffectiveToDate.setHours(0, 0, 0, 0);
                                                    const maxAllowedDate = new Date(originalCurrentEffectiveToDate);
                                                    maxAllowedDate.setDate(maxAllowedDate.getDate() + 1); // One day after Current Effective To
                                                    return maxAllowedDate;
                                                }
                                                
                                                return undefined;
                                            })()}
                                            className={formErrors.newEffectiveFrom ? "p-invalid" : ""}
                                            style={{ width: "100%" }}
                                        />
                                        {formErrors.newEffectiveFrom && (
                                            <small className="p-error">{formErrors.newEffectiveFrom}</small>
                                        )}
                                    </Col>
                                    <Col md={6} style={{ marginBottom: "1rem" }}>
                                        <Label htmlFor="newEffectiveTo">
                                            New Effective To <span style={{ color: "red" }}>*</span>
                                        </Label>
                                        <Calendar
                                            id="newEffectiveTo"
                                            value={formData.newEffectiveTo}
                                            onChange={(e) => {
                                                this.setState({
                                                    formData: { ...formData, newEffectiveTo: e.value as Date },
                                                    formErrors: { ...formErrors, newEffectiveTo: undefined },
                                                });
                                            }}
                                            dateFormat="dd/mm/yy"
                                            placeholder="dd/mm/yy"
                                            showIcon
                                            icon={<CalendarIcon size={16} />}
                                            appendTo={document.body}
                                            baseZIndex={1100}
                                            minDate={formData.newEffectiveFrom ? new Date(formData.newEffectiveFrom.getTime() + 24 * 60 * 60 * 1000) : undefined}
                                            className={formErrors.newEffectiveTo ? "p-invalid" : ""}
                                            style={{ width: "100%" }}
                                        />
                                        {formErrors.newEffectiveTo && (
                                            <small className="p-error">{formErrors.newEffectiveTo}</small>
                                        )}
                                    </Col>
                                </Row>
                            </>
                        ) : (
                            <Row>
                                <Col md={6} style={{ marginBottom: "1rem" }}>
                                    <Label htmlFor="effectiveFrom">
                                        Effective From <span style={{ color: "red" }}>*</span>
                                    </Label>
                                    <Calendar
                                        id="effectiveFrom"
                                        value={formData.effectiveFrom}
                                        onChange={(e) => {
                                            this.setState({
                                                formData: { ...formData, effectiveFrom: e.value as Date },
                                                formErrors: { ...formErrors, effectiveFrom: undefined },
                                            });
                                        }}
                                        dateFormat="dd/mm/yy"
                                        placeholder="dd/mm/yy"
                                        showIcon
                                        icon={<CalendarIcon size={16} />}
                                        appendTo={document.body}
                                        baseZIndex={1100}
                                        className={formErrors.effectiveFrom ? "p-invalid" : ""}
                                        style={{ width: "100%" }}
                                    />
                                    {formErrors.effectiveFrom && (
                                        <small className="p-error">{formErrors.effectiveFrom}</small>
                                    )}
                                </Col>
                                <Col md={6} style={{ marginBottom: "1rem" }}>
                                    <Label htmlFor="effectiveTo">
                                        Effective To <span style={{ color: "red" }}>*</span>
                                    </Label>
                                    <Calendar
                                        id="effectiveTo"
                                        value={formData.effectiveTo}
                                        onChange={(e) => {
                                            this.setState({
                                                formData: { ...formData, effectiveTo: e.value as Date },
                                                formErrors: { ...formErrors, effectiveTo: undefined },
                                            });
                                        }}
                                        dateFormat="dd/mm/yy"
                                        placeholder="dd/mm/yy"
                                        showIcon
                                        icon={<CalendarIcon size={16} />}
                                        appendTo={document.body}
                                        baseZIndex={1100}
                                        minDate={formData.effectiveFrom ? new Date(formData.effectiveFrom.getTime() + 24 * 60 * 60 * 1000) : undefined}
                                        className={formErrors.effectiveTo ? "p-invalid" : ""}
                                        style={{ width: "100%" }}
                                    />
                                    {formErrors.effectiveTo && (
                                        <small className="p-error">{formErrors.effectiveTo}</small>
                                    )}
                                </Col>
                            </Row>
                        )}

                        {/* All Versions for Bill Type Code - Only show in edit mode */}
                        {this.props.isEditMode && this.props.selectedBillTariff && (
                            <BillTariffVersionsList 
                                billTypeCode={this.props.selectedBillTariff.billTypeCode}
                                currentTariffId={this.props.selectedBillTariff.id}
                                visible={this.props.visible}
                            />
                        )}
                    </div>
                </Dialog>
            </>
        );
    }
}
